/* ONE BOUT — Limited page behaviour (Sprint 4 / L)
   <ob-detail-hotspots> (sections/tb-product-story.liquid)
     All labels are visible (Figma 280:2089 / 362:6435). A dot is a toggle button: hover (fine pointer), focus or
     tap marks its hotspot active (label + connector + dot highlighted and brought to the front); a second tap,
     Esc or a tap outside clears it. Tapping a label does the same as tapping its dot.
   <ob-rsvp-form> (sections/tb-rsvp-form.liquid)
     Client-side validation (first name, e-mail format, consent), messages from data-msg-* (section settings),
     then the Klaviyo client subscriptions API call of the previous RSVP form (company / list / source from
     data-*). Nothing is sent while a field is invalid. */

const FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');

class ObDetailHotspots extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.spots = [...this.querySelectorAll('.ob-hs')];
    this.active = null;
    this.pinned = false;

    this.spots.forEach((spot) => {
      const dot = spot.querySelector('.ob-hs__dot');
      const card = spot.querySelector('.ob-hs__card');
      if (!dot) return;
      dot.addEventListener('click', () => {
        if (this.active === spot && this.pinned) this.clear();
        else this.activate(spot, true);
      });
      dot.addEventListener('focus', () => { if (!this.pinned) this.activate(spot, false); });
      dot.addEventListener('blur', () => { if (!this.pinned && this.active === spot) this.clear(); });
      card?.addEventListener('click', () => this.activate(spot, true));
      spot.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse' && FINE_POINTER.matches && !this.pinned) this.activate(spot, false);
      });
      spot.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'mouse' && !this.pinned && this.active === spot) this.clear();
      });
    });

    this._onDocPointer = (e) => { if (this.active && !this.active.contains(e.target)) this.clear(); };
    this._onKey = (e) => {
      if (e.key !== 'Escape' || !this.active) return;
      const dot = this.active.querySelector('.ob-hs__dot');
      this.clear();
      dot?.focus({ preventScroll: true });
    };
    document.addEventListener('pointerdown', this._onDocPointer, true);
    document.addEventListener('keydown', this._onKey);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this._onDocPointer, true);
    document.removeEventListener('keydown', this._onKey);
    this._ready = false;
  }

  activate(spot, pin) {
    if (this.active && this.active !== spot) this.active.classList.remove('is-active');
    this.active = spot;
    this.pinned = pin;
    this.classList.add('has-active');
    spot.classList.add('is-active');
    this.spots.forEach((s) => s.querySelector('.ob-hs__dot')?.setAttribute('aria-pressed', String(s === spot)));
  }

  clear() {
    this.active?.classList.remove('is-active');
    this.active = null;
    this.pinned = false;
    this.classList.remove('has-active');
    this.spots.forEach((s) => s.querySelector('.ob-hs__dot')?.setAttribute('aria-pressed', 'false'));
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

class ObRsvpForm extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this.form = this.querySelector('form');
    if (!this.form) return;
    this._ready = true;
    this.errorEl = this.querySelector('.ob-lim-form__error');
    this.successEl = this.querySelector('.ob-lim-form__success');
    this.failureEl = this.querySelector('.ob-lim-form__failure');
    this.submitBtn = this.form.querySelector('[type="submit"]');
    this.fields = {
      first: this.form.elements.first_name,
      last: this.form.elements.last_name,
      email: this.form.elements.email,
      consent: this.form.elements.consent,
    };
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (this.sending) return;
      if (this.validate()) this.send();
    });
    this.form.addEventListener('input', (e) => this.clearField(e.target));
    this.form.addEventListener('change', (e) => this.clearField(e.target));
  }

  /* returns true when valid; otherwise marks the invalid fields, shows the first message, focuses the first field */
  validate() {
    const { first, email, consent } = this.fields;
    const invalid = [];
    let message = '';
    if (!first.value.trim() || !email.value.trim()) {
      if (!first.value.trim()) invalid.push(first);
      if (!email.value.trim()) invalid.push(email);
      message = this.dataset.msgRequired;
    }
    if (email.value.trim() && !EMAIL_RE.test(email.value.trim())) {
      invalid.push(email);
      message = message || this.dataset.msgEmail;
    }
    if (consent && !consent.checked) {
      invalid.push(consent);
      message = message || this.dataset.msgConsent;
    }
    [first, email, consent].forEach((f) => f && this.setInvalid(f, invalid.includes(f)));
    this.showError(invalid.length ? message : '');
    if (invalid.length) invalid[0].focus();
    return invalid.length === 0;
  }

  setInvalid(field, on) {
    if (on) {
      field.setAttribute('aria-invalid', 'true');
      if (this.errorEl) field.setAttribute('aria-errormessage', this.errorEl.id);
    } else {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-errormessage');
    }
  }

  clearField(field) {
    if (!field || field.getAttribute('aria-invalid') !== 'true') return;
    this.setInvalid(field, false);
    if (!this.form.querySelector('[aria-invalid="true"]')) this.showError('');
  }

  showError(text) {
    if (!this.errorEl) return;
    this.errorEl.textContent = text || '';
    this.errorEl.hidden = !text;
  }

  async send() {
    const { first, last, email } = this.fields;
    const label = this.submitBtn ? this.submitBtn.textContent : '';
    this.sending = true;
    if (this.failureEl) this.failureEl.hidden = true;
    if (this.submitBtn) {
      this.submitBtn.disabled = true;
      if (this.dataset.msgPending) this.submitBtn.textContent = this.dataset.msgPending;
    }
    try {
      const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.dataset.company)}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: email.value.trim(), first_name: first.value.trim(), last_name: last ? last.value.trim() : '' },
                },
              },
              custom_source: this.dataset.source,
            },
            relationships: { list: { data: { type: 'list', id: this.dataset.list } } },
          },
        }),
      });
      if (!res.ok && res.status !== 202) throw new Error(String(res.status));
      this.form.hidden = true;
      if (this.successEl) this.successEl.hidden = false;
    } catch (err) {
      if (this.failureEl) this.failureEl.hidden = false;
      if (this.submitBtn) {
        this.submitBtn.disabled = false;
        this.submitBtn.textContent = label;
      }
    } finally {
      this.sending = false;
    }
  }
}

if (!customElements.get('ob-detail-hotspots')) customElements.define('ob-detail-hotspots', ObDetailHotspots);
if (!customElements.get('ob-rsvp-form')) customElements.define('ob-rsvp-form', ObRsvpForm);
